<script setup lang="ts">
import { Plus } from 'lucide-vue-next'
import { useVModel } from '@vueuse/core'
import dayjs from 'dayjs'
import axios from 'axios'

import Email from '@/components/email/Email.vue'
import MarketingFlowExternalApiActionSettings from './MarketingFlowExternalApiActionSettings.vue'

import { isClickTriggeredAction, messageContainsTrackedLink, valueContainsTrackedLink } from '../clickTrigger'
import { sortActionsByPriority } from '../utils'

import StoreUrlsModal from '@/components/StoreUrlsModal.vue'
import { usePrefetchSurveysForTextareaVars } from '@/composables/usePrefetchSurveysForTextareaVars'
import { getCustomVars, getExternalApiCustomVars } from '../marketingFlowMessage'
import { extractSurveyIdsFromMessageContent } from '@/components/ui/TextareaWithVars/utils'

import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'
import type { Audience, LoyaltyReward } from '@/types/types'
import type { Store } from '@/views/contests/Components/types/global'
import type { ActionSettings, ActionType, MarketingAction } from '../types/marketingFlow'

const { t, locale } = useI18n()
const { hasPermissionBySlug } = useAccess()
const { listDomains } = useDomainConfig()
const { isActionStepCompleted, getActionStepErrors } = useMarketingFlow()
const { getEmailContent } = useEmailContent()
const { storesWithoutReviewLink } = useStore()
const { isSupportConnection } = useLocalStorage()

const isDomainReady = ref(false)
const emailComponent = ref<InstanceType<typeof Email>>()

const props = defineProps<{
  modelValue: ActionSettings
  stores: Store[]
  rewards: LoyaltyReward[]
  audiences: Audience[]
  isCheckingMessageLength: boolean
  messageLength?: number
  variablesLength?: Record<string, number>
  scenarioName?: string
  triggerType?: string | null
}>()
const selectedRewardId = ref<number | null>(null)

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: ActionSettings): void
  (e: 'goNextStep'): void
  (e: 'goPreviousStep'): void
  (e: 'onChanges', change: { isCritical: boolean; field?: string; value?: any }): void
  (e: 'fetchRewards'): void
  (e: 'update:isCheckingMessageLength', isCheckingMessageLength: boolean): void
  (e: 'calculate-message-length'): void
}>()

const isCheckingMessageLength = useVModel(props, 'isCheckingMessageLength', emit)

const warningExpanded = ref<boolean>(false)

const isActionModalOpen = ref<boolean>(false)
const isRewardModalOpen = ref<boolean>(false)
const isGooglePlaceIdModalOpen = ref<boolean>(false)

const partnerAccounts = ref([])
const isCreateEditRewardModalOpen = ref(false)

const {
  surveys,
  isLoading: surveysLoading,
  loadError: surveysError,
  fetchSurveys: fetchTextareaSurveys
} = usePrefetchSurveysForTextareaVars()

const selectedMultiplier = ref<number | null>(null)
const selectedMultiplierString = computed({
  get: () => (selectedMultiplier.value !== null ? String(selectedMultiplier.value) : ''),
  set: (v: string) => {
    selectedMultiplier.value = v !== '' ? Number(v) : null
  }
})

// Email editor modal
const { flags } = useFeatureFlag()
const showWebhookAction = computed(() => !!flags.value['marketing-flow-webhook']?.enabled)

const messageAction = computed<MarketingAction | undefined>(() =>
  props.modelValue.actions.find((action) => ['SEND_SMS', 'SEND_PUSH'].includes(action.actionType))
)

const messageLength = computed(() => props.messageLength ?? 0)
const variablesLength = computed(() => props.variablesLength ?? {})
const messageMaxLength = computed(() => {
  if (props.modelValue.isTransactional) return 160
  return messageAction.value?.actionType === 'SEND_SMS' ? 145 : 150
})
const textareaWithVarsI18nStrings = computed(() => ({
  textareaLabel: t('global.words.text'),
  characters: t('global.words.characters'),
  insertVar: t('global.customVars.insertLabel'),
  errorEmojiInMessage: t('ui.error.strings.unauthorizedEmoji'),
  customValue: {
    placeholder: t('global.customVars.customValue.placeholder'),
    create: t('global.customVars.customValue.add'),
    update: t('global.customVars.customValue.modify'),
    back: t('global.customVars.customValue.back')
  }
}))
const selectedReward = computed(() => props.rewards.find((reward) => reward.id === selectedRewardId.value))

const pointsAction = computed(() => props.modelValue.actions.find((action) => action.actionType === 'BONUS_POINTS'))
const multiplierAction = computed(() =>
  props.modelValue.actions.find((action) => action.actionType === 'BONUS_MULTIPLIER')
)
const rewardAction = computed(() => props.modelValue.actions.find((action) => action.actionType === 'REWARD'))
const emailAction = computed(() => props.modelValue.actions.find((action) => action.actionType === 'SEND_EMAIL'))
const externalApiAction = computed(() =>
  props.modelValue.actions.find((action) => action.actionType === 'CALL_EXTERNAL_API')
)

const emailTrackedLink = ref<boolean | null>(null)
const emailLinkRefresh = ref(0)

const smsHasTrackedLink = computed(() =>
  props.modelValue.actions.some(
    (action) => action.actionType === 'SEND_SMS' && messageContainsTrackedLink(action.messageContent)
  )
)

const canTriggerOnClick = computed(
  () =>
    smsHasTrackedLink.value ||
    emailTrackedLink.value === true ||
    (emailTrackedLink.value === null && props.modelValue.actions.some((action) => action.isTriggeredOnClick))
)

const refreshEmailTrackedLink = () => {
  emailLinkRefresh.value += 1
}

watch(
  [() => emailAction.value?.selectedEmailTemplate, emailLinkRefresh],
  async ([templateId]) => {
    if (!templateId) {
      emailTrackedLink.value = false
      return
    }

    const requestedId = templateId
    emailTrackedLink.value = null

    try {
      const content = await getEmailContent(requestedId)

      if (emailAction.value?.selectedEmailTemplate !== requestedId) {
        return
      }

      emailTrackedLink.value = valueContainsTrackedLink(content.structure)
    } catch {
      if (emailAction.value?.selectedEmailTemplate !== requestedId) {
        return
      }

      emailTrackedLink.value = props.modelValue.actions.some((action) => Boolean(action.isTriggeredOnClick))
    }
  },
  { immediate: true }
)

const sortedActions = computed(() => sortActionsByPriority(props.modelValue.actions))

const longestStoreName = computed(() => {
  return props.stores.reduce((longest, current) => (current.name.length > longest.name.length ? current : longest), {
    name: ''
  }).name
})

const customVariables = computed<CustomVars[]>(() =>
  getCustomVars({
    t,
    variablesLength: variablesLength.value,
    storeName: longestStoreName.value,
    reward: selectedReward.value as LoyaltyReward,
    pointValue: pointsAction.value?.bonusPoints
  })
)

const externalApiCustomVariables = computed<CustomVars[]>(() =>
  getExternalApiCustomVars({
    t,
    storeName: longestStoreName.value,
    pointValue: pointsAction.value?.bonusPoints,
    triggerType: props.triggerType
  })
)

const selectedStoresWithoutReviewLink = computed(() => {
  return props.stores.filter((store) => storesWithoutReviewLink.value.includes(store.id))
})

const updateModelValue = (obj: Partial<ActionSettings>, fieldName: keyof ActionSettings) => {
  const fieldValue = obj[fieldName]

  emit('update:modelValue', { ...props.modelValue, ...obj })
  emit('onChanges', { isCritical: true, field: fieldName, value: fieldValue })
}

const smsTypeOptions = computed(() => [
  {
    id: 'transactional',
    value: true,
    title: t('pages.marketingFlow.marketingFlowEditPage.action.smsType.transactionalTitle'),
    description: t('pages.marketingFlow.marketingFlowEditPage.action.smsType.transactionalDescription')
  },
  {
    id: 'promotional',
    value: false,
    title: t('pages.marketingFlow.marketingFlowEditPage.action.smsType.promotionalTitle'),
    description: t('pages.marketingFlow.marketingFlowEditPage.action.smsType.promotionalDescription')
  }
])

const selectedSmsType = computed(() =>
  smsTypeOptions.value.find((option) => option.value === props.modelValue.isTransactional)
)

const clickTriggerOptions = computed<RadioCardOption[]>(() => {
  const hasSms = props.modelValue.actions.some((action) => action.actionType === 'SEND_SMS')

  return [
    {
      id: 'on-click',
      value: true,
      title: t('pages.marketingFlow.marketingFlowEditPage.action.offerOnClick')
    },
    {
      id: 'on-trigger',
      value: false,
      title: t(
        hasSms
          ? 'pages.marketingFlow.marketingFlowEditPage.action.offerOnSmsSend'
          : 'pages.marketingFlow.marketingFlowEditPage.action.offerOnEmailSend'
      )
    }
  ]
})

const clickTriggerTitle = (actionType: ActionType) => {
  const titles: Partial<Record<ActionType, string>> = {
    BONUS_POINTS: 'pages.marketingFlow.marketingFlowEditPage.action.offerPointsTitle',
    REWARD: 'pages.marketingFlow.marketingFlowEditPage.action.offerRewardTitle',
    BONUS_MULTIPLIER: 'pages.marketingFlow.marketingFlowEditPage.action.offerMultiplierTitle',
    CALL_EXTERNAL_API: 'pages.marketingFlow.marketingFlowEditPage.action.offerApiTitle'
  }
  const key = titles[actionType]

  return key ? t(key) : ''
}

const selectedClickTrigger = (action: MarketingAction) =>
  clickTriggerOptions.value.find((option) => option.value === Boolean(action.isTriggeredOnClick))

const handleSmsTypeChange = (option: RadioCardOption | string | number) => {
  if (typeof option !== 'object') return

  updateModelValue({ isTransactional: option.value === true }, 'isTransactional')
}

const handleConversionDelayUpdate = (value: number | string) => {
  conversionDelay.value = value.toString()
  updateModelValue({ conversionDelay: value }, 'conversionDelay')
}

const handleActionBonusPointsUpdate = () => {
  emit('calculate-message-length')
  updateModelValue({ actions: props.modelValue.actions }, 'actions')
}

const handleActionUpdate = () => {
  emit('calculate-message-length')
  updateModelValue({ actions: props.modelValue.actions }, 'actions')
}

const setTriggeredOnClick = (action: MarketingAction, value: boolean) => {
  action.isTriggeredOnClick = value
  handleActionUpdate()
}

const handleClickTriggerChange = (action: MarketingAction, option: RadioCardOption | string | number) => {
  if (typeof option !== 'object') {
    return
  }

  setTriggeredOnClick(action, option.value === true)
}

watch(
  [canTriggerOnClick, emailTrackedLink],
  ([eligible, emailLink]) => {
    if (eligible || emailLink === null) {
      return
    }

    if (!props.modelValue.actions.some((action) => action.isTriggeredOnClick)) {
      return
    }

    updateModelValue(
      {
        actions: props.modelValue.actions.map((action) =>
          action.isTriggeredOnClick ? { ...action, isTriggeredOnClick: false } : action
        )
      },
      'actions'
    )
  },
  { immediate: true }
)

const hadTrackedLink = ref(false)
const clickDefaultsReady = ref(false)

const selectClickByDefault = () => {
  if (
    !props.modelValue.actions.some((action) => isClickTriggeredAction(action.actionType) && !action.isTriggeredOnClick)
  ) {
    return
  }

  updateModelValue(
    {
      actions: props.modelValue.actions.map((action) =>
        isClickTriggeredAction(action.actionType) ? { ...action, isTriggeredOnClick: true } : action
      )
    },
    'actions'
  )
}

watch(
  [smsHasTrackedLink, emailTrackedLink],
  () => {
    const emailPending = Boolean(emailAction.value?.selectedEmailTemplate) && emailTrackedLink.value === null

    if (emailPending) {
      return
    }

    const eligible = smsHasTrackedLink.value || emailTrackedLink.value === true

    if (!clickDefaultsReady.value) {
      hadTrackedLink.value = eligible
      clickDefaultsReady.value = true
      return
    }

    if (eligible && !hadTrackedLink.value) {
      selectClickByDefault()
    }

    hadTrackedLink.value = eligible
  },
  { immediate: true }
)

const handleExternalApiActionUpdate = (action: MarketingAction, patch: Partial<MarketingAction>) => {
  Object.assign(action, patch)
  handleActionUpdate()
}

const handleSelectReward = (rewardId: number) => {
  selectedRewardId.value = rewardId
  emit('fetchRewards')

  let newActions = props.modelValue.actions.map((action) => {
    if (action.actionType === 'SEND_SMS' && !action.messageContent?.includes('{{ rewardLink }}')) {
      action.messageContent += ' {{ rewardLink }}'
    }
    return action
  })
  const actionIndex = props.modelValue.actions.findIndex((action) => action.actionType === 'REWARD')

  newActions[actionIndex].selectedReward = rewardId

  emit('calculate-message-length')

  const updates = {
    actions: newActions,
    conversionDelay: Number(props.modelValue.conversionDelay)
  }

  emit('update:modelValue', { ...props.modelValue, ...updates })
  emit('onChanges', { isCritical: true, field: 'selectedReward', value: rewardId })

  isRewardModalOpen.value = false
  isCreateEditRewardModalOpen.value = false
}

const actionWithoutRewardText = () => {
  const copy = { ...props.modelValue }

  return copy.actions.map((action) => {
    if (action.actionType === 'SEND_SMS' || action.actionType === 'SEND_PUSH') {
      action.messageContent = action.messageContent.replaceAll('{{ rewardLink }}', '')
      action.messageContent = action.messageContent.replaceAll('{{ reward }}', '')
      action.messageContent = action.messageContent.replaceAll('{{ voucher }}', '')
      action.messageContent = action.messageContent.replaceAll(/  +/g, ' ')
    }

    return action
  })
}

const addAction = (type: ActionType) => {
  if (
    (messageAction.value && ['SEND_SMS', 'SEND_PUSH'].includes(type)) ||
    (pointsAction.value && type === 'BONUS_POINTS') ||
    (rewardAction.value && type === 'REWARD') ||
    (multiplierAction.value && type === 'BONUS_MULTIPLIER') ||
    (emailAction.value && type === 'SEND_EMAIL') ||
    (externalApiAction.value && type === 'CALL_EXTERNAL_API')
  ) {
    return
  }

  const newAction: MarketingAction = {
    actionType: type,
    messageContent: '',
    selectedReward: null,
    selectedEmailTemplate: null,
    bonusPoints: null,
    bonusMultiplier: null,
    preferPushChannel: false,
    preferSmsChannel: false,
    isTriggeredOnClick: canTriggerOnClick.value && isClickTriggeredAction(type),
    hasQrCodeLink: false,
    externalApiUrl: null,
    externalApiMethod: 'POST',
    externalApiHeaders: [{ key: '', value: '' }],
    externalApiBodyTemplate:
      '{\n  "id": "{{ customer.id }}",\n  "store": "{{ store.name }}",\n  "phone": "{{ customer.phone }}"\n}',
    externalApiAuthType: 'NONE',
    externalApiAuthConfig: null,
    isExternalApiAuthConfigDirty: false,
    isExternalApiAuthConfigSet: false
  }

  let newActions = props.modelValue.actions
  if (type === 'SEND_SMS' || type === 'SEND_PUSH' || type === 'SEND_EMAIL') {
    newActions = [newAction, ...newActions]
  } else {
    newActions.push(newAction)
  }

  isActionModalOpen.value = false

  emit('update:modelValue', {
    ...props.modelValue,
    actions: newActions
  })
  emit('onChanges', { isCritical: true, field: 'actions', value: newActions })
}

const removeAction = (index: number) => {
  const actionToRemove = sortedActions.value[index]
  let newActions = sortedActions.value
  if (actionToRemove.actionType === 'REWARD') {
    newActions = actionWithoutRewardText()
    selectedRewardId.value = null
  }

  if (actionToRemove.actionType === 'BONUS_MULTIPLIER') {
    selectedMultiplier.value = null
  }

  newActions = sortActionsByPriority(newActions)

  emit('calculate-message-length')
  updateModelValue({ actions: newActions.filter((_, i) => i !== index) }, 'actions')
}

const handleMultiplierUpdate = (value: number | null) => {
  selectedMultiplier.value = value

  if (value === -1) {
    return
  }

  const actionIndex = props.modelValue.actions.findIndex((action) => action.actionType === 'BONUS_MULTIPLIER')
  const newActions = [...props.modelValue.actions]
  newActions[actionIndex].bonusMultiplier = value

  updateModelValue({ actions: newActions }, 'actions')
}

const handleGooglePlaceIdModalUpdated = () => {
  isGooglePlaceIdModalOpen.value = false
}

const conversionDelay = ref<string | null>(null)

const handleTemplateSelectorSelect = (data: { templateId: string }) => {
  // Update the action with the new template ID
  const actionIndex = props.modelValue.actions.findIndex((action) => action.actionType === 'SEND_EMAIL')
  if (actionIndex !== -1) {
    const newActions = [...props.modelValue.actions]
    newActions[actionIndex] = { ...newActions[actionIndex], selectedEmailTemplate: data.templateId }

    emit('update:modelValue', { ...props.modelValue, actions: newActions })
    emit('onChanges', { isCritical: true, field: 'selectedEmailTemplate', value: data.templateId })
  }
}

const handleEmailTemplateSaved = (data: { templateId: string; templateName?: string }) => {
  // Update the SEND_EMAIL action with the template info
  const actionIndex = props.modelValue.actions.findIndex((action) => action.actionType === 'SEND_EMAIL')
  if (actionIndex !== -1) {
    const newActions = [...props.modelValue.actions]
    newActions[actionIndex] = { ...newActions[actionIndex], selectedEmailTemplate: data.templateId }

    emit('update:modelValue', { ...props.modelValue, actions: newActions })
    emit('onChanges', { isCritical: true, field: 'selectedEmailTemplate', value: data.templateId })
  }

  refreshEmailTrackedLink()
}

onMounted(async () => {
  const multiplierActionIndex = props.modelValue.actions.findIndex((action) => action.actionType === 'BONUS_MULTIPLIER')
  if (multiplierActionIndex !== -1) {
    const multiplier = props.modelValue.actions[multiplierActionIndex].bonusMultiplier
    selectedMultiplier.value = multiplier > 5 ? -1 : multiplier
  }

  conversionDelay.value = props.modelValue.conversionDelay.toString()

  const rewardActionIndex = props.modelValue.actions.findIndex((action) => action.actionType === 'REWARD')
  if (rewardActionIndex !== -1) {
    selectedRewardId.value = props.modelValue.actions[rewardActionIndex].selectedReward
  }

  emit('calculate-message-length')

  const domains = await listDomains()
  isDomainReady.value = domains.some((d) => d.is_ready_for_sending)

  if (hasPermissionBySlug('marketplace.read')) {
    await Promise.all([
      axios
        .get('/partner-parameters', {
          params: {
            hasRewardSupport: 1,
            isActive: 1
          }
        })
        .then(({ data }) => {
          partnerAccounts.value = data.data
        }),
      fetchTextareaSurveys()
    ])
  } else {
    await fetchTextareaSurveys()
  }
})

const actionStepErrors = computed(() => {
  return getActionStepErrors(
    props.modelValue,
    props.stores.map((store) => store.id)
  )
})

const bonusPointsError = computed(() => {
  const bonusPointsError = actionStepErrors.value.find((error) => error.field === 'bonusPoints')
  return bonusPointsError && pointsAction.value?.bonusPoints !== null
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${bonusPointsError.i18nKey}`)]
    : null
})

const bonusMultiplierError = computed(() => {
  const bonusMultiplierError = actionStepErrors.value.find((error) => error.field === 'bonusMultiplier')
  return bonusMultiplierError && multiplierAction.value?.bonusMultiplier !== null
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${bonusMultiplierError.i18nKey}`)]
    : null
})

const conversionDelayError = computed(() => {
  const conversionDelayError = actionStepErrors.value.find((error) => error.field === 'conversionDelay')
  return conversionDelayError && conversionDelay.value !== null
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${conversionDelayError.i18nKey}`)]
    : null
})

const externalApiUrlError = computed(() => {
  const externalApiUrlError = actionStepErrors.value.find((error) => error.field === 'externalApiUrl')
  return externalApiUrlError
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${externalApiUrlError.i18nKey}`)]
    : null
})

const externalApiMethodError = computed(() => {
  const externalApiMethodError = actionStepErrors.value.find((error) => error.field === 'externalApiMethod')
  return externalApiMethodError
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${externalApiMethodError.i18nKey}`)]
    : null
})

const externalApiHeadersError = computed(() => {
  const externalApiHeadersError = actionStepErrors.value.find((error) => error.field === 'externalApiHeaders')
  return externalApiHeadersError
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${externalApiHeadersError.i18nKey}`)]
    : null
})

const externalApiAuthTypeError = computed(() => {
  const externalApiAuthTypeError = actionStepErrors.value.find((error) => error.field === 'externalApiAuthType')
  return externalApiAuthTypeError
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${externalApiAuthTypeError.i18nKey}`)]
    : null
})

const externalApiAuthConfigError = computed(() => {
  const externalApiAuthConfigError = actionStepErrors.value.find((error) => error.field === 'externalApiAuthConfig')
  return externalApiAuthConfigError
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${externalApiAuthConfigError.i18nKey}`)]
    : null
})

const hasStoresWithoutReviewLinkError = computed(() => {
  return actionStepErrors.value.some(
    (error) => error.field === 'messageContent' && error.i18nKey === 'reviewLinkRequired'
  )
})

const handleMessageError = (error: boolean) => {
  const nextMessageError = error ? 'messageTooLong' : null
  if (props.modelValue.messageError === nextMessageError) {
    return
  }
  updateModelValue({ messageError: nextMessageError }, 'messageError')
}

const openEmailEditor = () => {
  emailComponent.value?.[0]?.openEmailEditor()
}

const messageSurvey = computed(() => {
  const messageAction = props.modelValue.actions.find(
    (action) => action.actionType === 'SEND_SMS' || action.actionType === 'SEND_PUSH'
  )
  if (!messageAction) return null

  const surveyIds = extractSurveyIdsFromMessageContent(messageAction.messageContent)

  return surveys.value?.find((survey) => survey.id === surveyIds[0])
})
</script>

<template>
  <div class="flex min-h-20 w-full flex-row items-center border-b bg-muted px-5 py-3">
    <div class="flex flex-1 flex-col justify-center">
      <span class="font-semibold text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.title') }}
      </span>
      <span class="text-sm uppercase text-muted-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.subTitle') }}
      </span>
    </div>

    <div class="flex flex-row items-center">
      <Button variant="outline" class="mx-3" @click="emit('goPreviousStep')">
        <span class="text-foreground">{{ t('global.words.back') }}</span>
      </Button>
      <Button
        variant="default-primary"
        :disabled="
          !isActionStepCompleted(
            modelValue,
            stores.map((store) => store.id)
          )
        "
        @click="emit('goNextStep')"
      >
        {{ t('global.words.continue') }}
      </Button>
    </div>
  </div>

  <div class="flex flex-col overflow-auto pt-5" style="height: calc(100% - 80px)">
    <div v-if="modelValue.actions.length === 0" class="w-full px-5">
      <Button variant="outline" class="mb-4 w-full py-3" @click="isActionModalOpen = true">
        <Plus />
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.selectAction') }}
      </Button>
    </div>

    <div class="mt-1 flex w-full flex-col">
      <div v-for="(action, index) in sortedActions" :key="'action-' + index" class="flex w-full flex-row pr-5">
        <div class="flex flex-col items-center px-4">
          <div v-if="index > 0" class="flex min-h-[14px] w-[1px] border-l"></div>
          <div
            class="flex min-h-7 w-7 items-center justify-center rounded-full border"
            :class="{ 'mt-[14px]': index === 0 }"
          >
            <BaseIcon
              v-if="action.actionType === 'SEND_SMS'"
              name="HeroChatBubbleLeftIconOutline"
              class="h-4 w-4 text-blue-600"
            />
            <BaseIcon
              v-if="action.actionType === 'SEND_PUSH'"
              name="HeroBellAlertIconOutline"
              class="h-4 w-4 text-blue-600"
            />
            <BaseIcon
              v-if="action.actionType === 'REWARD'"
              name="HeroGiftIconOutline"
              class="h-4 w-4 text-yellow-600"
            />
            <BaseIcon
              v-if="action.actionType === 'BONUS_POINTS'"
              name="HeroCircleStackIconOutline"
              class="h-4 w-4 text-yellow-600"
            />
            <BaseIcon
              v-if="action.actionType === 'BONUS_MULTIPLIER'"
              name="HeroCalculatorIconSolid"
              class="h-4 w-4 text-yellow-600"
            />
            <BaseIcon
              v-if="action.actionType === 'SEND_EMAIL'"
              name="HeroEnvelopeIconOutline"
              class="h-4 w-4 text-blue-600"
            />
            <BaseIcon
              v-if="action.actionType === 'CALL_EXTERNAL_API'"
              name="HeroGlobeAltIconOutline"
              class="h-4 w-4 text-blue-600"
            />
          </div>
          <div class="flex h-full w-[1px] flex-1 border-l"></div>
        </div>
        <div class="flex w-full flex-col">
          <div class="mb-4 flex flex-col rounded-lg border bg-muted p-4">
            <div class="flex flex-row items-center">
              <div v-if="action.actionType === 'SEND_SMS'" class="flex-1 font-medium text-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.action.sendSms') }}
              </div>
              <div v-if="action.actionType === 'SEND_PUSH'" class="flex-1 font-medium text-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.action.sendPush') }}
              </div>
              <div v-if="action.actionType === 'REWARD'" class="flex-1 font-medium text-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.action.giveReward') }}
              </div>
              <div v-if="action.actionType === 'BONUS_POINTS'" class="flex-1 font-medium text-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusPoints') }}
              </div>
              <div v-if="action.actionType === 'BONUS_MULTIPLIER'" class="flex-1 font-medium text-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusMultiplier') }}
              </div>
              <div v-if="action.actionType === 'SEND_EMAIL'" class="flex-1 font-medium text-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.action.sendEmail') }}
              </div>
              <div v-if="action.actionType === 'CALL_EXTERNAL_API'" class="flex-1 font-medium text-foreground">
                {{ t('pages.marketingFlow.marketingFlowEditPage.action.callExternalApi') }}
              </div>
              <div class="cursor-pointer" @click="removeAction(index)">
                <BaseIcon name="HeroTrashIconOutline" class="h-5 w-5 text-foreground-secondary hover:text-red-600" />
              </div>
            </div>

            <div v-if="['SEND_SMS', 'SEND_PUSH'].includes(action.actionType)">
              <div v-if="isSupportConnection && action.actionType === 'SEND_SMS'" class="mb-6 mt-4">
                <div class="text-sm font-medium text-foreground">
                  {{ t('pages.marketingFlow.marketingFlowEditPage.action.smsType.title') }}
                </div>
                <div class="mt-1 text-xs text-muted-foreground">
                  {{ t('pages.marketingFlow.marketingFlowEditPage.action.smsType.subtitle') }}
                </div>
                <AppRadioCard
                  class="mt-3"
                  :options="smsTypeOptions"
                  :model-value="selectedSmsType"
                  @update:model-value="handleSmsTypeChange"
                />
              </div>

              <TextareaWithVars
                ref="textareaWithVarsRef"
                v-model="action.messageContent"
                class="mb-6 mt-4"
                :label="t('global.words.text')"
                :max-length="messageMaxLength"
                :i18n-strings="textareaWithVarsI18nStrings"
                :custom-vars="customVariables"
                :sanitize-for-gsm="action.actionType === 'SEND_SMS'"
                :force-error="!isCheckingMessageLength && messageLength > messageMaxLength"
                :required-store-ids="stores.map((store) => store.id)"
                :surveys="surveys"
                :surveys-loading="surveysLoading"
                :surveys-error="surveysError"
                @has-error="handleMessageError"
                @update:model-value="handleActionUpdate"
              >
                <template #messageLength>
                  <span v-if="isCheckingMessageLength"> {{ t('global.words.loading') }}... </span>
                  <span v-else>
                    <span :class="{ 'text-red-600': messageLength > messageMaxLength }">
                      {{ messageLength }}/{{ messageMaxLength }} {{ t('global.words.characters') }}
                    </span>
                  </span>
                </template>

                <BaseTip
                  v-if="hasStoresWithoutReviewLinkError"
                  severity="error"
                  class="my-5"
                  :text="
                    t('global.storeUrlsModal.google.tip', {
                      count: selectedStoresWithoutReviewLink.length
                    })
                  "
                >
                  <template #button>
                    <button
                      class="w-max flex items-center text-sm font-medium text-red-600 cursor-pointer"
                      @click="isGooglePlaceIdModalOpen = true"
                    >
                      {{ t('global.storeUrlsModal.update') }}
                      <BaseIcon name="HeroArrowLongRightIconSolid" class="ml-3 h-5 w-5 shrink-0 text-red-600" />
                    </button>

                    <StoreUrlsModal
                      type="google"
                      :is-open="isGooglePlaceIdModalOpen"
                      :stores="selectedStoresWithoutReviewLink"
                      @close="isGooglePlaceIdModalOpen = false"
                      @updated="handleGooglePlaceIdModalUpdated"
                    />
                  </template>
                </BaseTip>
                <BaseTip
                  v-if="messageSurvey"
                  severity="warn"
                  class="my-5"
                  :text="
                    messageSurvey.endDate
                      ? t('global.survey.linkedMessageTipWithEndDate', {
                          startDate: dayjs(messageSurvey.launchDate * 1000).format('DD/MM/YYYY'),
                          endDate: dayjs(messageSurvey.endDate * 1000).format('DD/MM/YYYY')
                        })
                      : t('global.survey.linkedMessageTipFromStartDate', {
                          startDate: dayjs(messageSurvey.launchDate * 1000).format('DD/MM/YYYY')
                        })
                  "
                />
              </TextareaWithVars>
              <div v-if="action.actionType === 'SEND_SMS'" class="flex flex-row">
                <div class="mr-4 flex flex-1 flex-col">
                  <div class="text-sm">{{ t('pages.marketingFlow.marketingFlowEditPage.action.preferPushTitle') }}</div>
                  <div class="mt-1 text-xs text-foreground-secondary">
                    {{ t('pages.marketingFlow.marketingFlowEditPage.action.preferPushLabel') }}
                  </div>
                </div>
                <BaseSwitch
                  id="preferSmsChannel"
                  v-model="action.preferPushChannel"
                  @update:model-value="handleActionUpdate"
                />
              </div>

              <div v-if="action.actionType === 'SEND_PUSH'" class="flex flex-row">
                <div class="mr-2 flex flex-1 flex-col">
                  <div class="text-sm">{{ t('pages.marketingFlow.marketingFlowEditPage.action.preferSmsTitle') }}</div>
                  <div class="mt-1 text-xs text-foreground-secondary">
                    {{ t('pages.marketingFlow.marketingFlowEditPage.action.preferSmsLabel') }}
                  </div>
                </div>
                <BaseSwitch
                  id="preferSmsChannel"
                  v-model="action.preferSmsChannel"
                  @update:model-value="handleActionUpdate"
                />
              </div>

              <BaseTip
                v-if="locale === 'fr'"
                severity="info"
                class="mt-4 cursor-pointer"
                @click="warningExpanded = !warningExpanded"
              >
                <template #text>
                  <div v-if="modelValue.isTransactional" class="flex flex-row">
                    <div v-if="!warningExpanded" class="flex-1">
                      {{ t('pages.marketingFlow.marketingFlowEditPage.action.transactionalLaw') }}
                    </div>

                    <div v-else class="flex-1">
                      {{ t('pages.marketingFlow.marketingFlowEditPage.action.transactionalLawWarning') }}
                    </div>
                    <div class="flex w-5 items-center justify-center">
                      <BaseIcon
                        name="HeroChevronRightIconSolid"
                        class="h-5 w-5"
                        :class="{ 'rotate-90': warningExpanded }"
                      />
                    </div>
                  </div>
                  <div v-else class="flex flex-row">
                    <div v-if="!warningExpanded" class="flex-1">
                      {{ t('pages.marketingFlow.marketingFlowEditPage.action.marketingLaw') }}
                    </div>

                    <div v-else class="flex-1">
                      {{ t('pages.campaign.campaignCreatePage.block.warningInformation.text') }}
                    </div>
                    <div class="flex w-5 items-center justify-center">
                      <BaseIcon
                        name="HeroChevronRightIconSolid"
                        class="h-5 w-5"
                        :class="{ 'rotate-90': warningExpanded }"
                      />
                    </div>
                  </div>
                </template>
              </BaseTip>
            </div>

            <div v-if="action.actionType === 'REWARD'" class="mt-4 flex flex-col">
              <RewardSelector
                v-if="rewards"
                v-model:selected-reward-id="action.selectedReward"
                :validity-in-days="Number(modelValue.conversionDelay) || undefined"
                :show-validity-in-days="true"
                :rewards="rewards"
                :button-shown="['edit', 'change']"
                @select-reward="handleSelectReward"
                @update:validity-in-days="handleConversionDelayUpdate"
              />
            </div>

            <AppFormInput
              v-if="action.actionType === 'BONUS_POINTS'"
              id="field"
              v-model="action.bonusPoints"
              class="mt-4"
              :label="t('pages.marketingFlow.marketingFlowEditPage.action.givePointsNextVisit')"
              :errors="bonusPointsError"
              error-classes="text-xs"
              @update:model-value="handleActionBonusPointsUpdate"
            >
              <template #icon-right>
                <span class="text-sm text-muted-foreground">{{ t('global.words.points') }}</span>
              </template>
            </AppFormInput>

            <div v-if="action.actionType === 'BONUS_MULTIPLIER'" class="flex flex-col w-full">
              <label class="mt-4 mb-1.5 text-sm text-foreground-secondary">
                {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusMultiplierLabel') }}
              </label>

              <Select v-model="selectedMultiplierString" @update:model-value="handleMultiplierUpdate(Number($event))">
                <SelectTrigger class="h-10 w-full">
                  <SelectValue
                    :placeholder="t('pages.marketingFlow.marketingFlowEditPage.action.bonusMultiplierPlaceholder')"
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">{{ t('pages.marketingFlow.marketingFlowEditPage.action.x2') }}</SelectItem>
                  <SelectItem value="3">{{ t('pages.marketingFlow.marketingFlowEditPage.action.x3') }}</SelectItem>
                  <SelectItem value="4">{{ t('pages.marketingFlow.marketingFlowEditPage.action.x4') }}</SelectItem>
                  <SelectItem value="5">{{ t('pages.marketingFlow.marketingFlowEditPage.action.x5') }}</SelectItem>
                  <SelectItem value="-1">{{ t('pages.marketingFlow.marketingFlowEditPage.action.custom') }}</SelectItem>
                </SelectContent>
              </Select>

              <AppFormInput
                v-if="selectedMultiplier === -1"
                id="field"
                v-model="action.bonusMultiplier"
                class="mt-3"
                :errors="bonusMultiplierError"
                error-classes="text-xs"
                @update:model-value="handleActionUpdate"
              >
                <template #icon-right>
                  <span class="text-sm text-muted-foreground">
                    {{ t('pages.marketingFlow.marketingFlowEditPage.action.multiplier') }}
                  </span>
                </template>
              </AppFormInput>
            </div>

            <div v-if="action.actionType === 'SEND_EMAIL'" class="mt-4 flex flex-col">
              <Email
                ref="emailComponent"
                :selected-email-template="action.selectedEmailTemplate"
                @handle-email-template-saved="handleEmailTemplateSaved"
                @handle-template-selector-select="handleTemplateSelectorSelect"
              />
              <div>
                <Button
                  v-if="action.selectedEmailTemplate"
                  variant="outline"
                  class="bg-white text-foreground border-border hover:bg-accent"
                  @click="openEmailEditor"
                >
                  <BaseIcon name="IconoirPlus" class="h-4 w-4" />
                  {{ t('pages.marketingFlow.marketingFlowEditPage.action.editEmailTemplate') }}
                </Button>
              </div>
            </div>

            <MarketingFlowExternalApiActionSettings
              v-if="action.actionType === 'CALL_EXTERNAL_API'"
              :action="action"
              :custom-vars="externalApiCustomVariables"
              :url-errors="externalApiUrlError"
              :method-errors="externalApiMethodError"
              :headers-errors="externalApiHeadersError"
              :auth-type-errors="externalApiAuthTypeError"
              :auth-config-errors="externalApiAuthConfigError"
              @update="(patch) => handleExternalApiActionUpdate(action, patch)"
              @body-template-error="
                (val) => handleExternalApiActionUpdate(action, { externalApiBodyTemplateHasError: val })
              "
            />

            <div v-if="canTriggerOnClick && isClickTriggeredAction(action.actionType)" class="mt-4">
              <div class="mb-1.5 text-sm">
                {{ clickTriggerTitle(action.actionType) }}
              </div>
              <AppRadioCard
                class="grid grid-cols-1 gap-2.5"
                :options="clickTriggerOptions"
                :model-value="selectedClickTrigger(action)"
                @update:model-value="handleClickTriggerChange(action, $event)"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="modelValue.actions.length > 0">
        <div
          class="mb-4 flex cursor-pointer flex-row px-5 py-2 text-primary hover:text-primary/80"
          @click="isActionModalOpen = true"
        >
          <BaseIcon name="HeroPlusIconSolid" class="mr-2.5 h-5 w-5" />
          <span class="text-sm font-medium">{{ t('pages.marketingFlow.marketingFlowEditPage.action.addAction') }}</span>
        </div>
      </div>

      <div v-if="messageAction || rewardAction" class="mx-5 mb-5 rounded-lg border bg-muted p-4">
        <div class="mb-4 font-medium text-foreground">
          {{ t('pages.marketingFlow.marketingFlowEditPage.goal.conversionDelay') }}
        </div>
        <div class="mb-1.5 flex flex-row items-center">
          <div class="mr-2 flex flex-1">
            <span v-if="!pointsAction && !multiplierAction && !rewardAction" class="text-sm text-foreground">
              {{ t('pages.marketingFlow.marketingFlowEditPage.action.conversionDelay') }}
            </span>
            <span v-if="pointsAction || multiplierAction || rewardAction" class="text-sm text-foreground">
              {{ t('pages.marketingFlow.marketingFlowEditPage.action.conversionAndRewardDelay') }}
            </span>
          </div>
        </div>

        <AppFormInput
          id="field"
          v-model="conversionDelay"
          :errors="conversionDelayError"
          error-classes="text-xs"
          @update:model-value="handleConversionDelayUpdate"
        >
          <template #icon-right>
            <span class="text-sm text-muted-foreground">{{ t('global.words.days') }}</span>
          </template>
        </AppFormInput>

        <BaseTip
          v-if="selectedReward"
          severity="info"
          class="mt-4"
          :text="t('pages.marketingFlow.marketingFlowEditPage.action.infoConversionDelay')"
        />
      </div>
    </div>
  </div>

  <BaseModal
    :is-open="isActionModalOpen"
    heading-title="Actions disponibles"
    size="lg"
    @close="isActionModalOpen = false"
  >
    <div class="mb-2 flex flex-row text-xs font-normal uppercase text-muted-foreground">
      {{ t('pages.marketingFlow.marketingFlowEditPage.action.notification') }}
    </div>
    <div
      class="flex flex-row items-center rounded-lg p-2"
      :class="messageAction ? 'opacity-50' : 'cursor-pointer hover:bg-muted/50'"
      @click="addAction('SEND_SMS')"
    >
      <div class="mr-3 rounded-md p-1" :class="messageAction ? 'bg-muted' : 'bg-blue-50'">
        <BaseIcon
          name="HeroChatBubbleLeftIconOutline"
          class="h-4 w-4"
          :class="messageAction ? 'text-foreground-secondary' : 'text-blue-600'"
        />
      </div>
      <div class="text-sm text-foreground-secondary">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.sendSms') }}
      </div>
    </div>
    <div
      class="flex flex-row items-center rounded-lg p-2"
      :class="messageAction ? 'opacity-50' : 'cursor-pointer hover:bg-muted/50'"
      @click="addAction('SEND_PUSH')"
    >
      <div class="mr-3 rounded-md p-1" :class="messageAction ? 'bg-muted' : 'bg-blue-50'">
        <BaseIcon
          name="HeroBellAlertIconOutline"
          class="h-4 w-4"
          :class="messageAction ? 'text-foreground-secondary' : 'text-blue-600'"
        />
      </div>
      <div class="text-sm text-foreground-secondary">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.sendPush') }}
      </div>
    </div>
    <div
      class="flex flex-row items-center rounded-lg p-2"
      :class="emailAction ? 'opacity-50' : 'cursor-pointer hover:bg-muted/50'"
      @click="addAction('SEND_EMAIL')"
    >
      <div class="mr-3 rounded-md p-1" :class="emailAction ? 'bg-muted' : 'bg-blue-50'">
        <BaseIcon
          name="HeroEnvelopeIconOutline"
          class="h-4 w-4"
          :class="emailAction ? 'text-foreground-secondary' : 'text-blue-600'"
        />
      </div>
      <div class="text-sm text-foreground-secondary">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.sendEmail') }}
      </div>
    </div>
    <div
      v-if="showWebhookAction"
      class="flex flex-row items-center rounded-lg p-2"
      :class="externalApiAction ? 'opacity-50' : 'cursor-pointer hover:bg-muted/50'"
      @click="!externalApiAction && addAction('CALL_EXTERNAL_API')"
    >
      <div class="mr-3 rounded-md p-1" :class="externalApiAction ? 'bg-muted' : 'bg-blue-50'">
        <BaseIcon
          name="HeroGlobeAltIconOutline"
          class="h-4 w-4"
          :class="externalApiAction ? 'text-foreground-secondary' : 'text-blue-600'"
        />
      </div>
      <div class="text-sm text-foreground-secondary">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.callExternalApi') }}
      </div>
    </div>

    <div class="mb-2 mt-4 flex flex-row text-xs font-normal uppercase text-muted-foreground">
      {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonus') }}
    </div>

    <div
      class="flex flex-row items-center rounded-lg p-2"
      :class="rewardAction ? 'opacity-50' : 'cursor-pointer hover:bg-muted/50'"
      @click="addAction('REWARD')"
    >
      <div class="mr-3 rounded-md p-1" :class="rewardAction ? 'bg-muted' : 'bg-yellow-50'">
        <BaseIcon
          name="HeroGiftIconOutline"
          class="h-4 w-4"
          :class="rewardAction ? 'text-foreground-secondary' : 'text-yellow-600'"
        />
      </div>
      <div class="text-sm text-foreground-secondary">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.giveReward') }}
      </div>
    </div>

    <div
      class="flex flex-row items-center rounded-lg p-2"
      :class="pointsAction ? 'opacity-50' : 'cursor-pointer hover:bg-muted/50'"
      @click="addAction('BONUS_POINTS')"
    >
      <div class="mr-3 rounded-md p-1" :class="pointsAction ? 'bg-muted' : 'bg-yellow-50'">
        <BaseIcon
          name="HeroCircleStackIconOutline"
          class="h-4 w-4"
          :class="pointsAction ? 'text-foreground-secondary' : 'text-yellow-600'"
        />
      </div>
      <div class="text-sm text-foreground-secondary">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusPoints') }}
      </div>
    </div>
    <div
      class="flex flex-row items-center rounded-lg p-2"
      :class="multiplierAction ? 'opacity-50' : 'cursor-pointer hover:bg-muted/50'"
      @click="addAction('BONUS_MULTIPLIER')"
    >
      <div class="mr-3 rounded-md p-1" :class="multiplierAction ? 'bg-muted' : 'bg-yellow-50'">
        <BaseIcon
          name="HeroCalculatorIconSolid"
          class="h-4 w-4"
          :class="multiplierAction ? 'text-foreground-secondary' : 'text-yellow-600'"
        />
      </div>
      <div class="text-sm text-foreground-secondary">
        {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusMultiplier') }}
      </div>
    </div>
  </BaseModal>
</template>
